import { MouseEvent, ReactElement, useCallback } from 'react';

import {
  PrimaryButton,
  SecondaryButton,
} from '@changegps/horizon-component-library';
import CloseIcon from '@mui/icons-material/Close';
import {
  ButtonProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  Stack,
} from '@mui/material';

type ConfirmationModalProps = {
  open: boolean;
  isPending: boolean;
  title: DialogTitleProps['children'];
  content: DialogContentProps['children'];
  confirmActionTitle: string;
  cancelActionTitle: string;
  onClose: NonNullable<DialogProps['onClose']>;
  onConfirm: ButtonProps['onClick'];
  onCancel: ButtonProps['onClick'];
};

export const ConfirmationModal = (
  props: ConfirmationModalProps,
): ReactElement => {
  const handleClose = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      if (props.isPending) return;
      props.onClose(event, 'backdropClick');
    },
    [props],
  );

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="xs">
      <DialogTitle>{props.title}</DialogTitle>
      <IconButton
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: '#667085',
        }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          border: 'none',
          paddingTop: 0,
        }}
      >
        {props.content}
      </DialogContent>

      <DialogActions>
        <Stack width="100%" direction="row" spacing={1.5}>
          <SecondaryButton
            disabled={props.isPending}
            fullWidth
            onClick={handleClose}
          >
            {props.cancelActionTitle}
          </SecondaryButton>

          <PrimaryButton
            disabled={props.isPending}
            fullWidth
            onClick={props.onConfirm}
          >
            {props.isPending ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              props.confirmActionTitle
            )}
          </PrimaryButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
